<script setup>
import { useLayout } from '@/components/layouts/composables/layout';
import { Menu } from 'primevue';
import { ref } from "vue";
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useRoles } from '@/composables/useRoles';
import { useSelectedBrand } from '@/composables/useSelectedBrand';
import CompanyLogo from '../CompanyLogo.vue';
import { useEnvironment } from '@/composables/useEnvironment';

const { toggleMenu, toggleDarkMode, isDarkTheme } = useLayout();
const { isProduction, envLabel } = useEnvironment();
const router = useRouter();
const { currentUser, logout } = useAuth();
const { currentGroupLabel } = useRoles();

// Active brand (#2122): shown in the popover; switching happens on the dedicated
// brand page reached via the "Switch Brand" item below.
const { selectedBrand } = useSelectedBrand();

const toggle = (event) => {
    menu.value.toggle(event);
};

const handleLogout = () => {
    router.push({ name: 'logout' });
};

const menu = ref();
const items = ref([
    {
        label: 'Profile',
        items: [
            {
                label: 'Switch Brand',
                icon: 'pi pi-building',
                link: { name: 'selectBrand' }
            },
            {
                label: 'Settings',
                icon: 'pi pi-cog',
                link: { name: 'SettingsView' }
            },
            {
                label: 'Logout',
                icon: 'pi pi-sign-out',
                command: handleLogout
            }
        ]
    }
]);
</script>

<template>
    <div class="topbar-gradient-wrapper shadow-sm">
        <div class="layout-topbar">
            <div class="absolute w-full bottom-0 left-0"
                style="padding: 0.1rem; background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-color) 50%, rgba(33,150,243,0) 100%);">
            </div>

            <div class="layout-topbar-logo-container">
                <button class="layout-menu-button layout-topbar-action" @click="toggleMenu">
                    <i class="pi pi-bars"></i>
                </button>
                <router-link to="/" class="layout-topbar-logo">
                    <CompanyLogo />
                </router-link>
                <span v-if="!isProduction"
                    class="text-xs font-bold select-none self-end mb-1 ml-2 text-surface-400 dark:text-surface-400">
                    {{ envLabel }}
                </span>
            </div>

            <div class="layout-topbar-actions">
                <div class="layout-config-menu">
                    <button type="button" class="layout-topbar-action" @click="toggleDarkMode">
                        <i :class="['pi', { 'pi-moon': isDarkTheme, 'pi-sun': !isDarkTheme }]"></i>
                    </button>
                    <div class="flex items-center">
                        <button type="button" class="layout-topbar-action" icon="pi pi-user" @click="toggle"
                            aria-haspopup="true" aria-controls="overlay_menu">
                            <i class="pi pi-user"></i>
                        </button>
                        <Menu ref="menu" id="overlay_menu" :model="items" :popup="true">
                            <template #start>
                                <div class="px-3 py-2 border-b border-surface-200 dark:border-surface-700">
                                    <div class="font-medium text-sm">{{ currentUser?.name || currentUser?.email }}</div>
                                    <div class="text-xs text-text-secondary">{{ currentGroupLabel }}</div>
                                    <div class="text-xs text-text-secondary mt-1 flex items-center gap-1">
                                        <i class="pi pi-building" style="font-size: 0.7rem"></i>
                                        <span>{{ selectedBrand.name }}</span>
                                    </div>
                                </div>
                            </template>
                            <template #item="{ item }">
                                <!-- Handle router-link items -->
                                <router-link v-if="item.link" :to="item.link"
                                    class="flex items-center p-2 px-3 hover:bg-surface-hover">
                                    <i :class="item.icon" class="mr-2"></i>
                                    <span>{{ item.label }}</span>
                                </router-link>
                                <!-- Handle command/click items -->
                                <div v-else-if="item.command" @click="item.command"
                                    class="flex items-center p-2 px-3 hover:bg-surface-hover cursor-pointer">
                                    <i :class="item.icon" class="mr-2"></i>
                                    <span>{{ item.label }}</span>
                                </div>
                                <!-- Fallback for regular items -->
                                <div v-else class="flex items-center p-2 px-3">
                                    <i :class="item.icon" class="mr-2"></i>
                                    <span>{{ item.label }}</span>
                                </div>
                            </template>
                        </Menu>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
